import * as React from "react";
import { useState } from "react";
import axios from "axios";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Grid,
  Paper,
  Typography,
  Box,
  TextField,
  Divider,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import SearchIcon from "@material-ui/icons/Search";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import HourglassEmptyIcon from "@material-ui/icons/HourglassEmpty";
import CancelIcon from "@material-ui/icons/Cancel";
import BlockIcon from "@material-ui/icons/Block";
import FlashOnIcon from "@material-ui/icons/FlashOn";
import WarningIcon from "@material-ui/icons/Warning";
import HistoryIcon from "@material-ui/icons/History";
import WeekendIcon from "@material-ui/icons/Weekend";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import { Notification, useNotify } from "react-admin";
import { API } from "../Constant";
import { getConfigGet, getConfigPost } from "../getConfig";

const useStyles = makeStyles((theme) => ({
  main: {
    padding: 24,
    backgroundColor: "#f8f9fa",
    minHeight: "100vh",
  },
  title: { fontSize: 24, fontWeight: 600, marginBottom: 4 },
  subtitle: { fontSize: 14, color: "#666", marginBottom: 24 },
  searchPaper: { padding: 20, marginBottom: 16, borderRadius: 8 },
  searchRow: { display: "flex", alignItems: "center", gap: 12 },
  card: { marginBottom: 16, borderRadius: 8 },
  statBox: { textAlign: "center" as const, padding: 12, borderRadius: 8 },
  statValue: { fontSize: 18, fontWeight: 700 },
  statLabel: { fontSize: 11, color: "#666", marginTop: 2 },
  weekRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "8px 0",
    borderBottom: "1px solid #eee",
  },
  notEnrolled: {
    textAlign: "center" as const,
    padding: 24,
    color: "#999",
    fontSize: 14,
  },
  hintBanner: {
    padding: "12px 16px",
    backgroundColor: "#fff3e0",
    borderLeft: "4px solid #ff9800",
    borderRadius: 4,
    marginBottom: 16,
    display: "flex",
    alignItems: "center",
    gap: 12,
  },
  boostCard: {
    padding: "12px 16px",
    borderRadius: 8,
    marginBottom: 16,
  },
  auditRow: {
    padding: "8px 0",
    borderBottom: "1px solid #f0f0f0",
    display: "flex",
    alignItems: "flex-start",
    gap: 12,
  },
  auditDot: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    backgroundColor: "#1976d2",
    marginTop: 6,
    flexShrink: 0,
  },
  earlyWarning: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    color: "#e65100",
    fontSize: 11,
  },
}));

const statusColors: Record<string, { bg: string; color: string }> = {
  SHOWN: { bg: "#e3f2fd", color: "#1565c0" },
  OPTED_IN: { bg: "#fff3e0", color: "#e65100" },
  SPEND_CONFIRMED: { bg: "#f3e5f5", color: "#7b1fa2" },
  IN_PROGRESS: { bg: "#fff8e1", color: "#f57f17" },
  COMPLETED: { bg: "#e8f5e9", color: "#2e7d32" },
  FAILED: { bg: "#ffebee", color: "#c62828" },
  DISMISSED: { bg: "#fafafa", color: "#616161" },
  ACTIVE: { bg: "#e3f2fd", color: "#1565c0" },
  ACKNOWLEDGED: { bg: "#e8f5e9", color: "#2e7d32" },
  EXPIRED: { bg: "#ffebee", color: "#c62828" },
};

const weekStatusIcon = (status: string) => {
  if (status === "completed") return <CheckCircleIcon style={{ color: "#2e7d32", fontSize: 18 }} />;
  if (status === "in_progress") return <HourglassEmptyIcon style={{ color: "#f57f17", fontSize: 18 }} />;
  if (status === "failed") return <CancelIcon style={{ color: "#c62828", fontSize: 18 }} />;
  return <BlockIcon style={{ color: "#bbb", fontSize: 18 }} />;
};

const formatDate = (d: string | null) => d ? new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "-";

const ChallengeDashboard = () => {
  const classes = useStyles();
  const notify = useNotify();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<any>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [newTarget, setNewTarget] = useState("");
  const [newStartDate, setNewStartDate] = useState("");
  const [showAuditLog, setShowAuditLog] = useState(false);
  const [showBrands, setShowBrands] = useState(false);
  const [creditWeek, setCreditWeek] = useState("");
  const [creditAmount, setCreditAmount] = useState("");

  const handleAction = async (action: string, extra?: any) => {
    if (!email.trim()) return;
    setActionLoading(true);
    try {
      const headers = getConfigPost();
      const res: any = await axios.patch(
        `${API}/admin/user-challenges/0`,
        { email: email.trim(), challenge_type: "grocery", action, ...extra },
        { headers }
      );
      if (res?.data?.status) {
        notify(res.data.message || "Success", "info", "", undefined, 5000);
        handleSearch(); // Refresh
      } else {
        notify(res?.data?.message || "Failed", "error");
      }
    } catch (error: any) {
      notify(error?.response?.data?.message || "Action failed", "error");
    } finally {
      setActionLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!email.trim()) { notify("Enter an email", "error"); return; }
    setLoading(true);
    setData(null);
    try {
      const config = getConfigGet({});
      const res: any = await axios.get(
        `${API}/admin/user-challenges?query=${encodeURIComponent(email.trim())}`,
        config
      );
      if (res?.data?.status) {
        setData(res.data.data);
      } else {
        notify(res?.data?.message || "Not found", "error");
      }
    } catch (error: any) {
      notify(error?.response?.data?.message || "Failed", "error");
    } finally {
      setLoading(false);
    }
  };

  const gc = data?.grocery_challenge;
  const sc = data?.shopping_challenge;
  const ssc = data?.second_shop_challenge;

  return (
    <div className={classes.main}>
      <Typography className={classes.title}>Challenge Dashboard</Typography>
      <Typography className={classes.subtitle}>
        View grocery, shopping, and second shop challenge status for any user
      </Typography>

      <Paper className={classes.searchPaper} elevation={1}>
        <div className={classes.searchRow}>
          <TextField
            label="User Email or ID"
            variant="outlined"
            size="small"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyPress={(e) => e.key === "Enter" && handleSearch()}
            style={{ flex: 1 }}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={handleSearch}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}
          >
            Search
          </Button>
        </div>
      </Paper>

      {data && (
        <>
          <Paper className={classes.searchPaper} elevation={1}>
            <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
              {data.user.first_name} {data.user.last_name}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {data.user.email} | ID: {data.user.id}
            </Typography>
          </Paper>

          {/* Grocery Challenge */}
          <Card className={classes.card} elevation={1}>
            <CardHeader
              title={
                <Box display="flex" alignItems="center" style={{ gap: 10 }}>
                  <Typography variant="h6">Grocery Challenge</Typography>
                  {gc?.enrolled ? (
                    <Chip
                      size="small"
                      label={gc.stage_name}
                      style={{ backgroundColor: statusColors[gc.stage_name]?.bg || "#eee", color: statusColors[gc.stage_name]?.color || "#333", fontWeight: 600 }}
                    />
                  ) : (
                    <Chip size="small" label="NOT ENROLLED" style={{ backgroundColor: "#f5f5f5" }} />
                  )}
                </Box>
              }
            />
            <CardContent>
              {gc?.enrolled ? (
                <>
                  <Grid container spacing={2}>
                    <Grid item xs={6} sm={2}>
                      <Box className={classes.statBox} style={{ backgroundColor: "#e3f2fd" }}>
                        <Typography className={classes.statValue}>{gc.stage_name}</Typography>
                        <Typography className={classes.statLabel}>Stage {gc.stage}</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6} sm={2}>
                      <Box className={classes.statBox} style={{ backgroundColor: "#fff3e0" }}>
                        <Typography className={classes.statValue}>
                          {gc.confirmed_target ? `£${gc.confirmed_target}` : `£${gc.default_spend}`}
                        </Typography>
                        <Typography className={classes.statLabel}>
                          {gc.confirmed_target ? "Confirmed Target" : "Default Target"}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6} sm={2}>
                      <Box className={classes.statBox} style={{ backgroundColor: "#f3e5f5" }}>
                        <Typography className={classes.statValue}>W{gc.current_week}</Typography>
                        <Typography className={classes.statLabel}>Current Week</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6} sm={2}>
                      <Box className={classes.statBox} style={{ backgroundColor: "#e8f5e9" }}>
                        <Typography className={classes.statValue}>{gc.challenge_count}</Typography>
                        <Typography className={classes.statLabel}>Challenge #</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6} sm={2}>
                      <Box className={classes.statBox} style={{ backgroundColor: gc.boost_activated_at ? "#e8f5e9" : "#fafafa" }}>
                        <Typography className={classes.statValue}>{gc.boost_activated_at ? "Yes" : "No"}</Typography>
                        <Typography className={classes.statLabel}>Boost Activated</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6} sm={2}>
                      <Box className={classes.statBox} style={{ backgroundColor: "#fce4ec" }}>
                        <Typography className={classes.statValue}>{formatDate(gc.challenge_start)}</Typography>
                        <Typography className={classes.statLabel}>Started</Typography>
                      </Box>
                    </Grid>
                  </Grid>

                  {gc.stage >= 3 && (
                    <>
                      {/* Boost Status */}
                      <Divider style={{ margin: "16px 0" }} />
                      <Box className={classes.boostCard} style={{
                        backgroundColor: gc.boost_status === "active" ? "#e8f5e9" : gc.boost_status === "expired" ? "#fff3e0" : "#fafafa",
                        border: `1px solid ${gc.boost_status === "active" ? "#a5d6a7" : gc.boost_status === "expired" ? "#ffcc80" : "#e0e0e0"}`
                      }}>
                        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                          <FlashOnIcon style={{ color: gc.boost_status === "active" ? "#2e7d32" : gc.boost_status === "expired" ? "#e65100" : "#999" }} />
                          <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                            {gc.boost_status === "active" && `Boost Active — expires ${formatDate(gc.boost_expires_at)}`}
                            {gc.boost_status === "expired" && `Boost Expired on ${formatDate(gc.boost_expires_at)}`}
                            {gc.boost_status === "never_activated" && "Boost Never Activated"}
                            {!gc.boost_status && "Boost Unknown"}
                          </Typography>
                        </Box>
                      </Box>

                      {/* Week Schedule Table */}
                      <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: 8 }}>Week Schedule</Typography>
                      <Table size="small" style={{ marginBottom: 16 }}>
                        <TableHead>
                          <TableRow>
                            <TableCell style={{ fontWeight: 600, width: 70 }}>Week</TableCell>
                            <TableCell style={{ fontWeight: 600 }}>Date Range</TableCell>
                            <TableCell style={{ fontWeight: 600, width: 60 }}>Wknd</TableCell>
                            <TableCell style={{ fontWeight: 600 }}>Status</TableCell>
                            <TableCell style={{ fontWeight: 600 }}>Amount</TableCell>
                            <TableCell style={{ fontWeight: 600 }}>Completed</TableCell>
                            <TableCell style={{ fontWeight: 600, width: 120 }}>Action</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {(gc.weeks || []).map((w: any) => (
                            <React.Fragment key={w.week}>
                            <TableRow style={{ backgroundColor: w.admin_marked ? "#fffde7" : "transparent" }}>
                              <TableCell><strong>W{w.week}</strong></TableCell>
                              <TableCell>
                                <Typography variant="caption">
                                  {w.date_range ? `${formatDate(w.date_range.start)} → ${formatDate(w.date_range.end)}` : "-"}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                {w.includes_weekend ? (
                                  <Tooltip title="Includes weekend"><WeekendIcon style={{ color: "#2e7d32", fontSize: 18 }} /></Tooltip>
                                ) : (
                                  <Tooltip title="No full weekend"><WeekendIcon style={{ color: "#ccc", fontSize: 18 }} /></Tooltip>
                                )}
                              </TableCell>
                              <TableCell>
                                <Box display="flex" alignItems="center" style={{ gap: 4 }}>
                                  {weekStatusIcon(w.status)}
                                  <Chip size="small" label={w.status || "pending"}
                                    style={{ backgroundColor: statusColors[w.status?.toUpperCase()]?.bg || "#eee", color: statusColors[w.status?.toUpperCase()]?.color || "#333", fontSize: 11 }} />
                                  {w.admin_marked && (
                                    <Tooltip title="Marked complete by admin — dates auto-adjusted">
                                      <span className={classes.earlyWarning}><WarningIcon style={{ fontSize: 14 }} /> admin</span>
                                    </Tooltip>
                                  )}
                                </Box>
                              </TableCell>
                              <TableCell>£{parseFloat(w.amount || 0).toFixed(2)}</TableCell>
                              <TableCell>
                                <Typography variant="caption">{w.completed_at ? formatDate(w.completed_at) : "-"}</Typography>
                              </TableCell>
                              <TableCell>
                                <Box display="flex" style={{ gap: 4 }}>
                                  {w.status === "failed" && (
                                    <Button size="small" variant="contained" disabled={actionLoading}
                                      onClick={() => handleAction("unfail_week", { week: w.week })}
                                      style={{ fontSize: 9, padding: "2px 6px", backgroundColor: "#c62828", color: "#fff" }}>
                                      Unfail
                                    </Button>
                                  )}
                                  {w.status !== "completed" && w.status !== "pending" && (
                                    <>
                                      <Button size="small" variant="contained" color="primary" disabled={actionLoading}
                                        onClick={() => handleAction("mark_week_completed", { week: w.week, adjust_dates: false })}
                                        style={{ fontSize: 9, padding: "2px 6px" }}>
                                        Mark Complete
                                      </Button>
                                      <Button size="small" variant="outlined" color="primary" disabled={actionLoading}
                                        onClick={() => handleAction("mark_week_completed", { week: w.week, adjust_dates: true })}
                                        style={{ fontSize: 9, padding: "2px 6px" }}>
                                        Mark Complete & Mon-Sun Week
                                      </Button>
                                    </>
                                  )}
                                </Box>
                              </TableCell>
                            </TableRow>
                            {/* Purchases breakdown for this week */}
                            {w.purchases && w.purchases.length > 0 && (
                              <TableRow>
                                <TableCell colSpan={7} style={{ padding: "4px 16px 12px 40px", backgroundColor: "#fafafa" }}>
                                  <Typography variant="caption" style={{ fontWeight: 600, color: "#666" }}>
                                    Purchases ({w.purchases.length}):
                                  </Typography>
                                  {w.purchases.map((p: any, idx: number) => (
                                    <Typography key={idx} variant="caption" display="block" style={{ color: "#444", paddingLeft: 8 }}>
                                      £{parseFloat(p.amount).toFixed(2)} — {p.brand} — {formatDate(p.purchase_date)}
                                    </Typography>
                                  ))}
                                </TableCell>
                              </TableRow>
                            )}
                            </React.Fragment>
                          ))}
                        </TableBody>
                      </Table>

                      {/* Admin Actions */}
                      <Divider style={{ margin: "16px 0" }} />
                      <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: 12 }}>Admin Actions</Typography>

                      {/* Restart if challenge failed */}
                      {gc.stage === 7 && (
                        <Box style={{ marginBottom: 16, padding: "12px 16px", backgroundColor: "#ffebee", borderRadius: 8, border: "1px solid #ef9a9a" }}>
                          <Typography variant="body2" style={{ color: "#c62828", fontWeight: 600, marginBottom: 8 }}>Challenge Failed</Typography>
                          <Button variant="contained" size="small" disabled={actionLoading}
                            onClick={() => handleAction("restart_challenge")}
                            style={{ backgroundColor: "#c62828", color: "#fff" }}>
                            Restart Challenge
                          </Button>
                        </Box>
                      )}

                      <Grid container spacing={2}>
                        {/* Add Credit */}
                        <Grid item xs={12} sm={6}>
                          <Box style={{ padding: "12px 16px", backgroundColor: "#f5f5f5", borderRadius: 8, height: "100%" }}>
                            <Typography variant="caption" style={{ fontWeight: 600, color: "#666", marginBottom: 8, display: "block" }}>
                              Add Credit to Week
                            </Typography>
                            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                              <TextField label="Week" variant="outlined" size="small" type="number"
                                value={creditWeek} onChange={(e) => setCreditWeek(e.target.value)}
                                style={{ width: 70 }} inputProps={{ min: 1, max: 4 }} />
                              <TextField label="Amount (£)" variant="outlined" size="small" type="number"
                                value={creditAmount} onChange={(e) => setCreditAmount(e.target.value)}
                                style={{ width: 110 }} inputProps={{ min: 1 }} />
                              <Button variant="contained" size="small" color="primary" disabled={actionLoading || !creditWeek || !creditAmount}
                                onClick={() => { handleAction("add_credit", { week: parseInt(creditWeek), amount: creditAmount }); setCreditAmount(""); }}>
                                Add
                              </Button>
                            </div>
                          </Box>
                        </Grid>

                        {/* Change Target */}
                        <Grid item xs={12} sm={6}>
                          <Box style={{ padding: "12px 16px", backgroundColor: "#f5f5f5", borderRadius: 8, height: "100%" }}>
                            <Typography variant="caption" style={{ fontWeight: 600, color: "#666", marginBottom: 8, display: "block" }}>
                              Change Weekly Target
                            </Typography>
                            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                              <TextField label="New Target (£)" variant="outlined" size="small" type="number"
                                value={newTarget} onChange={(e) => setNewTarget(e.target.value)}
                                style={{ width: 130 }} inputProps={{ min: 10, max: 500 }} />
                              <Button variant="contained" size="small" color="primary" disabled={actionLoading || !newTarget}
                                onClick={() => { handleAction("change_weekly_target", { new_target: newTarget }); setNewTarget(""); }}>
                                Update
                              </Button>
                            </div>
                          </Box>
                        </Grid>

                        {/* Change Start Date */}
                        <Grid item xs={12} sm={6}>
                          <Box style={{ padding: "12px 16px", backgroundColor: "#f5f5f5", borderRadius: 8, height: "100%" }}>
                            <Typography variant="caption" style={{ fontWeight: 600, color: "#666", marginBottom: 8, display: "block" }}>
                              Change Start Date
                            </Typography>
                            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                              <TextField label="Start Date" variant="outlined" size="small" type="date"
                                value={newStartDate} onChange={(e) => setNewStartDate(e.target.value)}
                                InputLabelProps={{ shrink: true }} style={{ width: 160 }} />
                              <Button variant="contained" size="small" color="primary" disabled={actionLoading || !newStartDate}
                                onClick={() => { handleAction("change_start_date", { new_start_date: newStartDate }); setNewStartDate(""); }}>
                                Apply
                              </Button>
                            </div>
                          </Box>
                        </Grid>

                        {/* Extend Week */}
                        <Grid item xs={12} sm={6}>
                          <Box style={{ padding: "12px 16px", backgroundColor: "#f5f5f5", borderRadius: 8, height: "100%" }}>
                            <Typography variant="caption" style={{ fontWeight: 600, color: "#666", marginBottom: 8, display: "block" }}>
                              Extend Current Week
                            </Typography>
                            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                              <Button variant="outlined" size="small" disabled={actionLoading}
                                onClick={() => handleAction("extend_week", { extra_days: 1 })}>+1 Day</Button>
                              <Button variant="outlined" size="small" disabled={actionLoading}
                                onClick={() => handleAction("extend_week", { extra_days: 3 })}>+3 Days</Button>
                              <Button variant="outlined" size="small" disabled={actionLoading}
                                onClick={() => handleAction("extend_week", { extra_days: 7 })}>+7 Days</Button>
                            </div>
                          </Box>
                        </Grid>
                      </Grid>

                      {/* Audit Log */}
                      {gc.audit_log && gc.audit_log.length > 0 && (
                        <>
                          <Divider style={{ margin: "16px 0" }} />
                          <Box display="flex" alignItems="center" style={{ gap: 8, cursor: "pointer" }}
                            onClick={() => setShowAuditLog(!showAuditLog)}>
                            <HistoryIcon style={{ fontSize: 18, color: "#666" }} />
                            <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                              Audit Log ({gc.audit_log.length} entries)
                            </Typography>
                            {showAuditLog ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          </Box>
                          <Collapse in={showAuditLog}>
                            <Box style={{ marginTop: 8, maxHeight: 300, overflowY: "auto" }}>
                              {gc.audit_log.map((entry: any) => (
                                <div key={entry.id} className={classes.auditRow}>
                                  <div className={classes.auditDot} />
                                  <Box>
                                    <Typography variant="body2" style={{ fontWeight: 600 }}>
                                      {entry.action.replace(/_/g, " ")}
                                    </Typography>
                                    <Typography variant="caption" color="textSecondary">
                                      {entry.admin_email} — {formatDate(entry.createdAt)}
                                    </Typography>
                                    {entry.old_value && (
                                      <Typography variant="caption" display="block" style={{ color: "#c62828" }}>
                                        Before: {JSON.stringify(entry.old_value)}
                                      </Typography>
                                    )}
                                    {entry.new_value && (
                                      <Typography variant="caption" display="block" style={{ color: "#2e7d32" }}>
                                        After: {JSON.stringify(entry.new_value)}
                                      </Typography>
                                    )}
                                  </Box>
                                </div>
                              ))}
                            </Box>
                          </Collapse>
                        </>
                      )}

                      {/* Qualifying Grocery Brands */}
                      {data?.qualifying_grocery_brands && data.qualifying_grocery_brands.length > 0 && (
                        <>
                          <Divider style={{ margin: "16px 0" }} />
                          <Box display="flex" alignItems="center" style={{ gap: 8, cursor: "pointer" }}
                            onClick={() => setShowBrands(!showBrands)}>
                            <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                              Qualifying Grocery Brands ({data.qualifying_grocery_brands.length})
                            </Typography>
                            {showBrands ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          </Box>
                          <Collapse in={showBrands}>
                            <Box style={{ marginTop: 8, display: "flex", flexWrap: "wrap", gap: 4 }}>
                              {data.qualifying_grocery_brands.map((brand: string) => (
                                <Chip key={brand} label={brand} size="small" style={{ fontSize: 11 }} />
                              ))}
                            </Box>
                          </Collapse>
                        </>
                      )}
                    </>
                  )}
                </>
              ) : (
                <div className={classes.notEnrolled}>Not enrolled in grocery challenge</div>
              )}
            </CardContent>
          </Card>

          {/* Shopping Challenge */}
          <Card className={classes.card} elevation={1}>
            <CardHeader
              title={
                <Box display="flex" alignItems="center" style={{ gap: 10 }}>
                  <Typography variant="h6">Shopping Challenge</Typography>
                  {sc?.enrolled ? (
                    <Chip
                      size="small"
                      label={sc.status}
                      style={{ backgroundColor: statusColors[sc.status]?.bg || "#eee", color: statusColors[sc.status]?.color || "#333", fontWeight: 600 }}
                    />
                  ) : (
                    <Chip size="small" label="NOT ENROLLED" style={{ backgroundColor: "#f5f5f5" }} />
                  )}
                </Box>
              }
            />
            <CardContent>
              {sc?.enrolled ? (
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={2}>
                    <Box className={classes.statBox} style={{ backgroundColor: "#e3f2fd" }}>
                      <Typography className={classes.statValue}>{sc.status}</Typography>
                      <Typography className={classes.statLabel}>Status</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={2}>
                    <Box className={classes.statBox} style={{ backgroundColor: "#fff3e0" }}>
                      <Typography className={classes.statValue}>{formatDate(sc.challenge_started_at)}</Typography>
                      <Typography className={classes.statLabel}>Started</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={2}>
                    <Box className={classes.statBox} style={{ backgroundColor: "#fce4ec" }}>
                      <Typography className={classes.statValue}>{formatDate(sc.challenge_expires_at)}</Typography>
                      <Typography className={classes.statLabel}>Expires</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={2}>
                    <Box className={classes.statBox} style={{ backgroundColor: sc.completed_at ? "#e8f5e9" : "#fafafa" }}>
                      <Typography className={classes.statValue}>{sc.completed_at ? formatDate(sc.completed_at) : "-"}</Typography>
                      <Typography className={classes.statLabel}>Completed</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={2}>
                    <Box className={classes.statBox} style={{ backgroundColor: "#f3e5f5" }}>
                      <Typography className={classes.statValue}>{sc.purchase_brand || "-"}</Typography>
                      <Typography className={classes.statLabel}>Brand ({sc.purchase_amount ? `£${sc.purchase_amount}` : "-"})</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={2}>
                    <Box className={classes.statBox} style={{ backgroundColor: sc.boost_activated_at ? "#e8f5e9" : "#fafafa" }}>
                      <Typography className={classes.statValue}>
                        {sc.boost_activated_at ? <FlashOnIcon style={{ color: "#2e7d32" }} /> : "No"}
                      </Typography>
                      <Typography className={classes.statLabel}>
                        {sc.boost_expires_at ? `Until ${formatDate(sc.boost_expires_at)}` : "Boost"}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              ) : (
                <div className={classes.notEnrolled}>Not enrolled in shopping challenge</div>
              )}
            </CardContent>
          </Card>

          {/* Second Shop Challenge */}
          <Card className={classes.card} elevation={1}>
            <CardHeader
              title={
                <Box display="flex" alignItems="center" style={{ gap: 10 }}>
                  <Typography variant="h6">Second Shop Challenge</Typography>
                  {ssc?.enrolled ? (
                    <Chip
                      size="small"
                      label={ssc.stage_name}
                      style={{ backgroundColor: statusColors[ssc.stage_name]?.bg || "#eee", color: statusColors[ssc.stage_name]?.color || "#333", fontWeight: 600 }}
                    />
                  ) : (
                    <Chip size="small" label="NOT ENROLLED" style={{ backgroundColor: "#f5f5f5" }} />
                  )}
                </Box>
              }
            />
            <CardContent>
              {ssc?.enrolled ? (
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={2}>
                    <Box className={classes.statBox} style={{ backgroundColor: "#e3f2fd" }}>
                      <Typography className={classes.statValue}>{ssc.stage_name}</Typography>
                      <Typography className={classes.statLabel}>Stage {ssc.stage}</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={2}>
                    <Box className={classes.statBox} style={{ backgroundColor: "#fff3e0" }}>
                      <Typography className={classes.statValue}>{formatDate(ssc.challenge_started_at)}</Typography>
                      <Typography className={classes.statLabel}>Started</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={2}>
                    <Box className={classes.statBox} style={{ backgroundColor: "#fce4ec" }}>
                      <Typography className={classes.statValue}>{formatDate(ssc.challenge_expires_at)}</Typography>
                      <Typography className={classes.statLabel}>Expires</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={2}>
                    <Box className={classes.statBox} style={{ backgroundColor: ssc.completed_at ? "#e8f5e9" : "#fafafa" }}>
                      <Typography className={classes.statValue}>{ssc.completed_at ? formatDate(ssc.completed_at) : "-"}</Typography>
                      <Typography className={classes.statLabel}>Completed</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={2}>
                    <Box className={classes.statBox} style={{ backgroundColor: "#f3e5f5" }}>
                      <Typography className={classes.statValue}>{ssc.purchase_brand || "-"}</Typography>
                      <Typography className={classes.statLabel}>Brand ({ssc.purchase_amount ? `£${ssc.purchase_amount}` : "-"})</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={2}>
                    <Box className={classes.statBox} style={{ backgroundColor: ssc.boost_activated_at ? "#e8f5e9" : "#fafafa" }}>
                      <Typography className={classes.statValue}>
                        {ssc.boost_activated_at ? <FlashOnIcon style={{ color: "#2e7d32" }} /> : "No"}
                      </Typography>
                      <Typography className={classes.statLabel}>
                        {ssc.boost_expires_at ? `Until ${formatDate(ssc.boost_expires_at)}` : "Boost"}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              ) : (
                <div className={classes.notEnrolled}>Not enrolled in second shop challenge</div>
              )}
            </CardContent>
          </Card>
        </>
      )}

      <Notification />
    </div>
  );
};

export default ChallengeDashboard;
